import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';

import {
  BlueprintRevealStatus,
  Sport,
} from '#src/__generated__/globalTypes.ts';
import { drawImage } from '#src/components/card/Overlay/utils/drawImage.ts';
import { drawProgressBar } from '#src/components/card/Overlay/utils/drawProgressBar.ts';
import { dateProgress } from '#src/lib/dates.ts';
import { withFragments } from '#src/lib/gql/index.ts';

import type { earlyAccessOverlay2025_AnyCardInterface } from './__generated__/index.graphql.ts';
import delayedUrl from './assets/delayed.webp';
import delayedMajorUrl from './assets/delayedMajor.webp';
import lockedUrl from './assets/locked.webp';
import lockedMajorUrl from './assets/lockedMajor.webp';

export const earlyAccessOverlay2025 = withFragments(
  (card_: earlyAccessOverlay2025_AnyCardInterface) => {
    const card = card_;

    const isMajor =
      card.sport === Sport.NBA || card.cardEditionName === 'blueprint-major';

    const { blueprint } = card;
    if (!blueprint) {
      return undefined;
    }
    return async (image: ImageBitmap) => {
      const canvas = document.createElement('canvas');
      const WIDTH = image.width;
      const HEIGHT = image.height;

      canvas.width = WIDTH;
      canvas.height = HEIGHT;

      const state = blueprint.revealStatus;
      const progress = blueprint.revealDate
        ? dateProgress(
            blueprint.launchDate ?? new Date(2025, 6, 15),
            blueprint.revealDate
          ) / 100
        : 0;

      const ctx = canvas.getContext('2d')!;
      ctx.clearRect(0, 0, WIDTH, HEIGHT);
      // draw texture
      ctx.drawImage(image, 0, 0);

      const promises: Promise<void>[] = [];

      const revealable = [
        BlueprintRevealStatus.REVEALABLE,
        BlueprintRevealStatus.REVEALED,
      ].includesValue(state);

      if (isMajor) {
        ctx.save();
        const gradient = ctx.createLinearGradient(135, 0, 535, 0);
        gradient.addColorStop(0, '#ba8d26');
        gradient.addColorStop(0.5, '#F6CD4E');
        gradient.addColorStop(1, '#FFF2CA');
        ctx.fillStyle = gradient;

        const cappedProgress = Math.min(0.9, progress);

        ctx.roundRect(
          135,
          818,
          (revealable ? 1 : cappedProgress) * 500,
          25,
          25
        );
        ctx.fill();
        ctx.restore();
      } else {
        const STEPS = 20;

        const stepsFilled = revealable
          ? STEPS
          : Math.min(STEPS - 1, Math.max(1, Math.floor(progress * STEPS)));
        promises.push(
          drawProgressBar({
            ctx,
            stepsFilled,
            boundingBox: {
              left: 0.17,
              top: 0.645,
              width: 0.58,
              height: 0.038,
            },
            options: {
              steps: 20,
              gap: 4,
              border: 0,
              color: '#ba8d26',
            },
          })
        );
      }

      if (state === BlueprintRevealStatus.DELAYED) {
        promises.push(
          drawImage({
            ctx,
            imageUrl: isMajor ? delayedMajorUrl : delayedUrl,
            boundingBox: {
              left: 0,
              top: 0,
              width: 1,
              height: 1,
            },
          })
        );
      }

      if (state === BlueprintRevealStatus.UNREVEALABLE) {
        promises.push(
          drawImage({
            ctx,
            imageUrl: isMajor ? lockedMajorUrl : lockedUrl,
            boundingBox: {
              left: 0,
              top: 0,
              width: 1,
              height: 1,
            },
          })
        );
      }

      await Promise.all(promises);

      return createImageBitmap(canvas);
    };
  },
  {
    AnyCardInterface: gql`
      fragment earlyAccessOverlay2025_AnyCardInterface on AnyCardInterface {
        slug
        cardEditionName
        sport
        blueprint {
          id
          launchDate
          revealDate
          revealStatus
        }
      }
    ` as TypedDocumentNode<earlyAccessOverlay2025_AnyCardInterface>,
  }
);
